import React, { useState, useEffect } from 'react';
import platformMetricsService from '../../../services/platformMetricsService';
import Icon from '../../../components/AppIcon';
import { useKeyboardNavigation } from '../../../utils/accessibility';

const PlatformMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeListings: 0,
    totalBookings: 0,
    monthlyRevenue: 0,
    pendingDisputes: 0,
    verificationRequests: 0,
    bookingVolume: { confirmed: 0, completed: 0, cancelled: 0, pending: 0 },
    userStatistics: { renters: 0, owners: 0, admins: 0, newUsersThisPeriod: 0 },
    revenueTrends: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchMetrics();
  }, [timeRange]);

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = platformMetricsService?.subscribeToMetrics((tableName) => {
      console.log(`Real-time update from ${tableName}`);
      fetchMetrics();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [timeRange]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const data = await platformMetricsService?.getMetrics(timeRange);
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      title: 'Total Users',
      value: metrics?.totalUsers,
      icon: 'Users',
      color: 'blue',
      trend: '+12%',
      trendUp: true,
      description: `${metrics?.userStatistics?.newUsersThisPeriod} new this period`
    },
    {
      title: 'Active Listings',
      value: metrics?.activeListings,
      icon: 'Package',
      color: 'purple',
      trend: '+8%',
      trendUp: true,
      description: 'Available trailers'
    },
    {
      title: 'Total Bookings',
      value: metrics?.totalBookings,
      icon: 'Calendar',
      color: 'green',
      trend: '+15%',
      trendUp: true,
      description: `${metrics?.bookingVolume?.confirmed} confirmed`
    },
    {
      title: 'Revenue',
      value: `$${metrics?.monthlyRevenue?.toLocaleString()}`,
      icon: 'DollarSign',
      color: 'orange',
      trend: '+23%',
      trendUp: true,
      description: 'Total revenue'
    },
    {
      title: 'Pending Disputes',
      value: metrics?.pendingDisputes,
      icon: 'AlertTriangle',
      color: 'red',
      trend: '-5%',
      trendUp: false,
      description: 'Requires attention'
    },
    {
      title: 'Verification Requests',
      value: metrics?.verificationRequests,
      icon: 'ShieldCheck',
      color: 'indigo',
      trend: '+3%',
      trendUp: true,
      description: 'Pending verification'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500/10 text-blue-500',
      purple: 'bg-purple-500/10 text-purple-500',
      green: 'bg-green-500/10 text-green-500',
      orange: 'bg-orange-500/10 text-orange-500',
      red: 'bg-red-500/10 text-red-500',
      indigo: 'bg-indigo-500/10 text-indigo-500'
    };
    return colors?.[color] || colors?.blue;
  };

  const handleRefresh = useKeyboardNavigation(
    () => fetchMetrics(),
    () => fetchMetrics(),
    null
  );

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground mb-1 flex items-center">
            <Icon name="BarChart3" size={24} className="text-primary mr-2" aria-hidden="true" />
            Platform Metrics
          </h2>
          <p className="font-body text-sm text-muted-foreground">Real-time analytics and key performance indicators</p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center space-x-2 bg-muted rounded-lg p-1" role="group" aria-label="Time range selection">
          {['7d', '30d', '90d']?.map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              aria-pressed={timeRange === range}
              aria-label={`Show ${range === '7d' ? '7 days' : range === '30d' ? '30 days' : '90 days'} data`}
              className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                timeRange === range
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-card'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="status" aria-label="Loading metrics">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="bg-muted animate-pulse rounded-lg h-32" aria-hidden="true" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="region" aria-label="Platform metrics cards">
            {metricCards?.map((metric, index) => (
              <article
                key={index}
                className="bg-muted/50 rounded-lg p-5 border border-border hover:shadow-elevation-3 transition-smooth focus-within:ring-2 focus-within:ring-ring"
                aria-labelledby={`metric-title-${index}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(metric?.color)}`} aria-hidden="true">
                    <Icon name={metric?.icon} size={24} strokeWidth={2} />
                  </div>
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                    metric?.trendUp ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
                  }`} aria-label={`Trend: ${metric?.trend}`}>
                    <Icon name={metric?.trendUp ? 'TrendingUp' : 'TrendingDown'} size={14} aria-hidden="true" />
                    <span>{metric?.trend}</span>
                  </div>
                </div>
                <h3 id={`metric-title-${index}`} className="font-body text-sm text-muted-foreground mb-1">{metric?.title}</h3>
                <p className="font-heading text-3xl font-bold text-foreground">{metric?.value}</p>
                <p className="font-body text-xs text-muted-foreground mt-1">{metric?.description}</p>
              </article>
            ))}
          </div>

          {/* User Statistics Breakdown */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg" role="region" aria-labelledby="user-stats-heading">
            <h3 id="user-stats-heading" className="font-heading text-base font-semibold text-foreground mb-3">User Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="font-body text-sm text-muted-foreground">Renters</p>
                <p className="font-heading text-2xl font-bold text-foreground">{metrics?.userStatistics?.renters}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Owners</p>
                <p className="font-heading text-2xl font-bold text-foreground">{metrics?.userStatistics?.owners}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Admins</p>
                <p className="font-heading text-2xl font-bold text-foreground">{metrics?.userStatistics?.admins}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">New Users</p>
                <p className="font-heading text-2xl font-bold text-foreground">{metrics?.userStatistics?.newUsersThisPeriod}</p>
              </div>
            </div>
          </div>

          {/* Booking Volume Breakdown */}
          <div className="mt-4 p-4 bg-muted/30 rounded-lg" role="region" aria-labelledby="booking-volume-heading">
            <h3 id="booking-volume-heading" className="font-heading text-base font-semibold text-foreground mb-3">Booking Volume</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="font-body text-sm text-muted-foreground">Confirmed</p>
                <p className="font-heading text-2xl font-bold text-green-600">{metrics?.bookingVolume?.confirmed}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Completed</p>
                <p className="font-heading text-2xl font-bold text-blue-600">{metrics?.bookingVolume?.completed}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Pending</p>
                <p className="font-heading text-2xl font-bold text-orange-600">{metrics?.bookingVolume?.pending}</p>
              </div>
              <div>
                <p className="font-body text-sm text-muted-foreground">Cancelled</p>
                <p className="font-heading text-2xl font-bold text-red-600">{metrics?.bookingVolume?.cancelled}</p>
              </div>
            </div>
          </div>
        </>
      )}
      {/* System Health Indicator */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" aria-hidden="true" />
            <div>
              <p className="font-body text-sm font-semibold text-foreground">System Status: Operational</p>
              <p className="font-body text-xs text-muted-foreground">All services running normally • Real-time updates active</p>
            </div>
          </div>
          <button
            onClick={fetchMetrics}
            onKeyDown={handleRefresh}
            aria-label="Refresh metrics data"
            className="flex items-center space-x-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Icon name="RefreshCw" size={16} aria-hidden="true" />
            <span className="font-body text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformMetrics;